import { Stack } from '@mui/joy';
import { mapValues } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { ActionButton, AgGridServerTable } from 'core/components';
import { LabeledValue } from 'core/elements';
import { AccessPermissions, ActionButtonType } from 'core/enums';
import { AgGridServerTableProvider, useStepsContext } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { useAddBomPartsToQuoteContext } from 'quotes/components/AddBomPartsToQuote';
import { getColumnDef } from 'quotes/components/VendorQuoteList';
import { getQuoteStatuses } from 'quotes/components/VendorQuoteList/utils';
import { QuoteEntityType, QuoteListType, QuoteStatus } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { quoteService } from 'quotes/services';
import { IBaseQuote } from 'quotes/types';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { getInitialState } from './utils';

interface IProps {
  sourceQuote?: IBaseQuote;
  entityType?: QuoteEntityType;
}

export const SelectVendorQuote = ({ sourceQuote, entityType = QuoteEntityType.QUOTE }: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  const { goToNextStep } = useStepsContext();
  const { selectedContact, setSelectedQuote } = useAddBomPartsToQuoteContext();

  // Same rule as the vendor quote list's Trade Agreement columns (bulk Send as SPA button).
  const showTradeAgreementInfo =
    VendorQuoteHelper.isAlternateExportEnabled({ user }) &&
    hasPermission(AccessPermissions.SEND_AS_SPA);

  const columnDefs = useMemo(
    () =>
      getColumnDef(entityType, {
        quoteListType: QuoteListType.COPY_PARTS_TO_QUOTE,
        showTradeAgreementInfo,
      }),
    [entityType, showTradeAgreementInfo]
  );

  const initialState = useMemo(() => getInitialState(entityType), [entityType]);

  const tableId = useMemo(() => `vendor${entityType}ListTable`, [entityType]);

  const tableEntityType = useMemo(() => `Vendor${entityType}`, [entityType]);

  const onSelectionChanged = useCallback(
    ({ api }) => {
      const gridSelectedRows = api?.getSelectedNodes();

      setSelectedQuote(gridSelectedRows[0]?.data);
    },
    [setSelectedQuote]
  );

  const mapFilters = useCallback((filters) => {
    let mappedFilters = filters;

    mappedFilters = mapValues(mappedFilters, (value, key) => {
      switch (key) {
        case 'status':
          return value.join('|');
        default:
          return value;
      }
    });

    return mappedFilters;
  }, []);

  // Imported VQs are never valid destinations. The BE drops an empty `status` filter altogether
  // (Base_ListManager::skipFilter), so the guard has to be an explicit inclusion list — an
  // exclusion that empties the value would silently reopen the hole.
  const selectableQuoteStatuses = useMemo(
    () => getQuoteStatuses(QuoteEntityType.QUOTE, { excludeImported: true }),
    []
  );

  const getRows = useCallback(
    async ({ filters, ...rest }: AgGridGetRowsParams) => {
      const mappedFilters = mapFilters(filters);

      let statusFilter = mappedFilters?.status || '';

      if (QuoteEntityType.ORDER === entityType) {
        if (!statusFilter) {
          statusFilter = [
            QuoteStatus.ORDER_DRAFT_VENDOR,
            QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER,
          ].join('|');
        }
      } else {
        const selectedStatuses: QuoteStatus[] = statusFilter
          .split('|')
          .filter((status: string) => status && QuoteStatus.EDITABLE_BY_CUSTOMER !== status);

        statusFilter = (selectedStatuses.length ? selectedStatuses : selectableQuoteStatuses).join(
          '|'
        );
      }

      return await quoteService.getVendorQuotes({
        filters: {
          ...mappedFilters,
          status: statusFilter,
          isOrder: QuoteEntityType.ORDER === entityType ? 1 : 0,
          vendorContactName: selectedContact.name,
          ...(sourceQuote?.id ? { excludeIds: [sourceQuote.id] } : {}),
        },
        ...rest,
      });
    },
    [entityType, mapFilters, selectableQuoteStatuses, selectedContact.name, sourceQuote?.id]
  );

  // Belt to the request filter: an Imported VQ that slips through (stale row cache, a filter the
  // user narrowed by hand) still cannot be picked. Always true on the ORDER entity.
  const isRowSelectable = useCallback(
    ({ data }) =>
      !VendorQuoteHelper.isImportedVendorQuote({ quote: data, quoteEntityType: entityType }),
    [entityType]
  );

  const actionButtons = useMemo(
    () => [
      <ActionButton
        key="addNewQuote"
        action={{
          label: t('buttonLabelCreateNewVendorQuote', { entityType }),
          type: ActionButtonType.BUTTON,
          color: Color.SUCCESS,
          variant: Variant.SOLID,
          onClick: () => {
            setSelectedQuote(null);
            goToNextStep();
          },
        }}
      />,
    ],
    [entityType, goToNextStep, setSelectedQuote, t]
  );

  return (
    <>
      <AgGridServerTableProvider
        tableId={tableId}
        entityType={tableEntityType}
        columnDefs={columnDefs}
        initialState={initialState}
        getRows={getRows}
        onSelectionChanged={onSelectionChanged}
        isRowSelectable={isRowSelectable}
        selectionMode="singleRow"
        enableClickSelection
      >
        <Stack spacing={1} sx={{ display: 'flex', height: '100%' }}>
          <LabeledValue
            label={t('labelVendorQuoteContact', { entityType })}
            value={selectedContact?.name}
            options={{
              labelLevel: Level.BODY_SM,
              valueLevel: Level.TITLE_MD,
              wrapperProps: {
                spacing: 0.5,
                direction: Direction.ROW,
                alignItems: 'center',
              },
            }}
          />

          <AgGridServerTable actionButtons={actionButtons} />
        </Stack>
      </AgGridServerTableProvider>
    </>
  );
};
